import React, { useState } from 'react';
import { Layout } from '../components/Layout/Layout';
import { SearchIcon, FilterIcon, MapPinIcon, DollarSignIcon, CalendarIcon, ThumbsUpIcon, ThumbsDownIcon, BarChart2Icon, MapIcon, SlidersIcon, BookmarkIcon, Share2Icon, HeartIcon, ScanSearchIcon } from 'lucide-react';
import { ProjectLocationMap, ProjectLocation } from '../components/Maps/ProjectLocationMap';
import { ProjectComparisonTool } from '../components/Interactive/ProjectComparisonTool';
import { motion, AnimatePresence } from 'framer-motion';
export const ProjectDiscoveryPage = () => {
  const [activeTab, setActiveTab] = useState('recommended');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showComparison, setShowComparison] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(false);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [likedProjects, setLikedProjects] = useState<number[]>([]);
  const [dislikedProjects, setDislikedProjects] = useState<number[]>([]);
  const [savedProjects, setSavedProjects] = useState<number[]>([]);
  // Sample project data
  const projects = [{
    id: 1,
    title: 'Hydroponic Vegetable Farm',
    location: 'Cameron Highlands, Pahang',
    lat: 4.4733,
    lng: 101.3889,
    image: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    description: 'State-of-the-art hydroponic facility producing leafy greens and vegetables for local markets and export to Singapore.',
    funding: 'RM 750,000',
    return: '15-18% ROI',
    timeline: '24 months',
    match: 92,
    tags: ['Technology', 'Sustainable', 'Highland'],
    type: 'Technology'
  }, {
    id: 2,
    title: 'Organic Durian Orchard',
    location: 'Raub, Pahang',
    lat: 3.7899,
    lng: 101.857,
    image: 'https://images.unsplash.com/photo-1601493700631-2b16ec4b4716?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1035&q=80',
    description: 'Expanding organic durian plantation specializing in premium Musang King variety for local consumption and export to China.',
    funding: 'RM 420,000',
    return: '12-14% ROI',
    timeline: '36 months',
    match: 87,
    tags: ['Organic', 'Fruit', 'Export'],
    type: 'Durian'
  }, {
    id: 3,
    title: 'Smart Irrigation System',
    location: 'MADA Region, Kedah',
    lat: 6.1184,
    lng: 100.3728,
    image: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
    description: 'Implementing AI-driven irrigation technology to reduce water usage by 40% in paddy fields within the MADA irrigation scheme.',
    funding: 'RM 600,000',
    return: '16-20% ROI',
    timeline: '18 months',
    match: 95,
    tags: ['Technology', 'Water Conservation', 'Innovation'],
    type: 'Technology'
  }, {
    id: 4,
    title: 'Heritage Rice Cultivation',
    location: 'Bario, Sarawak',
    lat: 3.7436,
    lng: 115.4617,
    image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1632&q=80',
    description: 'Reviving heritage Bario rice varieties using traditional farming methods and developing premium rice products for specialty markets.',
    funding: 'RM 380,000',
    return: '10-12% ROI',
    timeline: '48 months',
    match: 78,
    tags: ['Heritage', 'Indigenous', 'Specialty'],
    type: 'Paddy'
  }, {
    id: 5,
    title: 'Sustainable Palm Oil Plantation',
    location: 'Johor Bahru, Johor',
    lat: 1.4927,
    lng: 103.7414,
    image: 'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1472&q=80',
    description: 'RSPO-certified palm oil plantation transitioning to sustainable practices with on-site processing for specialty palm oil products.',
    funding: 'RM 950,000',
    return: '13-15% ROI',
    timeline: '36 months',
    match: 82,
    tags: ['Sustainable', 'RSPO-Certified', 'Export'],
    type: 'Palm'
  }];
  // Convert projects to map locations
  const mapLocations: ProjectLocation[] = projects.map(project => ({
    id: project.id,
    title: project.title,
    description: project.description,
    lat: project.lat,
    lng: project.lng,
    type: project.type,
    image: project.image
  }));
  // Toggle project like/dislike
  const toggleLike = (projectId: number) => {
    if (likedProjects.includes(projectId)) {
      setLikedProjects(likedProjects.filter(id => id !== projectId));
    } else {
      setLikedProjects([...likedProjects, projectId]);
      setDislikedProjects(dislikedProjects.filter(id => id !== projectId));
    }
  };
  const toggleDislike = (projectId: number) => {
    if (dislikedProjects.includes(projectId)) {
      setDislikedProjects(dislikedProjects.filter(id => id !== projectId));
    } else {
      setDislikedProjects([...dislikedProjects, projectId]);
      setLikedProjects(likedProjects.filter(id => id !== projectId));
    }
  };
  // Toggle saved project
  const toggleSaved = (projectId: number) => {
    if (savedProjects.includes(projectId)) {
      setSavedProjects(savedProjects.filter(id => id !== projectId));
    } else {
      setSavedProjects([...savedProjects, projectId]);
    }
  };
  // Handle map marker click
  const handleMarkerClick = (project: ProjectLocation) => {
    setSelectedProject(project.id);
  };
  return <Layout data-id="element-1227">
      <div className="container mx-auto px-4 py-6 md:py-8" data-id="element-1228">
        <div className="mb-4 md:mb-6" data-id="element-1229">
          <h1 className="text-2xl md:text-3xl font-bold text-soil-brown" data-id="element-1230">
            Discover Projects
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-1231">
            Find agricultural projects that match your interests and investment
            criteria
          </p>
        </div>
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 mb-4 md:mb-6" data-id="element-1232">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4" data-id="element-1233">
            <div className="flex-1 relative" data-id="element-1234">
              <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1235" />
              <input type="text" placeholder="Search for agricultural projects..." className="input-field pl-10 w-full" data-id="element-1236" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1237">
              <button className="btn btn-outline flex items-center text-sm md:text-base" onClick={() => setAdvancedFilters(!advancedFilters)} data-id="element-1238">
                <FilterIcon size={16} className="mr-1 md:mr-2" data-id="element-1239" />
                Filters
              </button>
              <button className="btn btn-outline flex items-center text-sm md:text-base" onClick={() => setShowComparison(true)} data-id="element-1240">
                <ScanSearchIcon size={16} className="mr-1 md:mr-2" data-id="element-1241" />
                Compare
              </button>
              <div className="flex border rounded-lg overflow-hidden ml-auto md:ml-0" data-id="element-1242">
                <button className={`p-2 ${viewMode === 'list' ? 'bg-leaf-green text-white' : 'bg-gray-100'}`} onClick={() => setViewMode('list')} aria-label="List view" data-id="element-1243">
                  <BarChart2Icon size={16} data-id="element-1244" />
                </button>
                <button className={`p-2 ${viewMode === 'map' ? 'bg-leaf-green text-white' : 'bg-gray-100'}`} onClick={() => setViewMode('map')} aria-label="Map view" data-id="element-1245">
                  <MapIcon size={16} data-id="element-1246" />
                </button>
              </div>
            </div>
          </div>
          {/* Advanced Filters */}
          <AnimatePresence data-id="element-1247">
            {advancedFilters && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-1248">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mt-4 pt-4 border-t" data-id="element-1249">
                  <div data-id="element-1250">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1251">
                      Project Type
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1252">
                      <option value="" data-id="element-1253">All Types</option>
                      <option value="paddy" data-id="element-1254">Paddy Farming</option>
                      <option value="palm" data-id="element-1255">Palm Oil</option>
                      <option value="durian" data-id="element-1256">Durian</option>
                      <option value="technology" data-id="element-1257">AgTech</option>
                      <option value="organic" data-id="element-1258">Organic</option>
                      <option value="aquaculture" data-id="element-1259">Aquaculture</option>
                    </select>
                  </div>
                  <div data-id="element-1260">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1261">
                      Location
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1262">
                      <option value="" data-id="element-1263">All Locations</option>
                      <option value="peninsula" data-id="element-1264">Peninsular Malaysia</option>
                      <option value="borneo" data-id="element-1265">Sabah & Sarawak</option>
                      <option value="north" data-id="element-1266">Northern Region</option>
                      <option value="south" data-id="element-1267">Southern Region</option>
                      <option value="east" data-id="element-1268">East Coast</option>
                    </select>
                  </div>
                  <div data-id="element-1269">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1270">
                      Investment Range
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1271">
                      <option value="" data-id="element-1272">Any Amount</option>
                      <option value="small" data-id="element-1273">Under RM 200,000</option>
                      <option value="medium" data-id="element-1274">RM 200,000 - RM 500,000</option>
                      <option value="large" data-id="element-1275">RM 500,000 - RM 1,000,000</option>
                      <option value="xlarge" data-id="element-1276">Over RM 1,000,000</option>
                    </select>
                  </div>
                  <div data-id="element-1277">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1278">
                      Expected Return
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1279">
                      <option value="" data-id="element-1280">Any Return</option>
                      <option value="low" data-id="element-1281">Under 10% ROI</option>
                      <option value="medium" data-id="element-1282">10-15% ROI</option>
                      <option value="high" data-id="element-1283">Over 15% ROI</option>
                    </select>
                  </div>
                  <div data-id="element-1284">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1285">
                      Timeline
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1286">
                      <option value="" data-id="element-1287">Any Duration</option>
                      <option value="short" data-id="element-1288">Under 12 months</option>
                      <option value="medium" data-id="element-1289">12-24 months</option>
                      <option value="long" data-id="element-1290">24-36 months</option>
                      <option value="verylong" data-id="element-1291">Over 36 months</option>
                    </select>
                  </div>
                  <div data-id="element-1292">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1293">
                      Match Score
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1294">
                      <option value="" data-id="element-1295">Any Match</option>
                      <option value="excellent" data-id="element-1296">Excellent (90%+)</option>
                      <option value="good" data-id="element-1297">Good (80-89%)</option>
                      <option value="fair" data-id="element-1298">Fair (70-79%)</option>
                      <option value="poor" data-id="element-1299">Poor (Below 70%)</option>
                    </select>
                  </div>
                  <div data-id="element-1300">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1301">
                      Sustainability
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1302">
                      <option value="" data-id="element-1303">Any</option>
                      <option value="organic" data-id="element-1304">Organic</option>
                      <option value="rspo" data-id="element-1305">RSPO Certified</option>
                      <option value="water" data-id="element-1306">Water Conservation</option>
                      <option value="renewable" data-id="element-1307">Renewable Energy</option>
                    </select>
                  </div>
                  <div data-id="element-1308">
                    <label className="block text-gray-700 text-sm font-medium mb-1 md:mb-2" data-id="element-1309">
                      Project Stage
                    </label>
                    <select className="input-field text-sm py-1.5" data-id="element-1310">
                      <option value="" data-id="element-1311">Any Stage</option>
                      <option value="startup" data-id="element-1312">Startup</option>
                      <option value="growth" data-id="element-1313">Growth</option>
                      <option value="expansion" data-id="element-1314">Expansion</option>
                      <option value="mature" data-id="element-1315">Mature</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end mt-4 pt-4 border-t" data-id="element-1316">
                  <button className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 mr-2 text-sm py-1.5 px-3" data-id="element-1317">
                    Reset Filters
                  </button>
                  <button className="btn btn-primary text-sm py-1.5 px-3" data-id="element-1318">
                    Apply Filters
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
        {/* Tabs */}
        <div className="flex border-b mb-4 md:mb-6 overflow-x-auto scrollbar-hide" data-id="element-1319">
          {['recommended', 'newest', 'trending', 'saved'].map(tab => <button key={tab} className={`py-2 px-4 md:py-3 md:px-6 font-medium capitalize whitespace-nowrap text-sm md:text-base ${activeTab === tab ? 'border-b-2 border-leaf-green text-leaf-green' : 'text-gray-600 hover:text-leaf-green/70'}`} onClick={() => setActiveTab(tab)} data-id="element-1320">
              {tab}
              {tab === 'saved' && savedProjects.length > 0 && <span className="ml-1 md:ml-2 bg-leaf-green text-white text-xs px-1.5 py-0.5 rounded-full" data-id="element-1321">
                  {savedProjects.length}
                </span>}
            </button>)}
        </div>
        {/* Map View */}
        {viewMode === 'map' && <div className="mb-6" data-id="element-1322">
            <ProjectLocationMap locations={mapLocations} height="350px md:500px" onMarkerClick={handleMarkerClick} data-id="element-1323" />
            {/* Selected project details */}
            {selectedProject && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mt-4 bg-white rounded-lg shadow-md p-3 md:p-4" data-id="element-1324">
                {projects.filter(p => p.id === selectedProject).map(project => <div key={project.id} className="flex flex-col md:flex-row gap-4" data-id="element-1325">
                      <div className="w-full md:w-1/4 h-48 md:h-auto" data-id="element-1326">
                        <img src={project.image} alt={project.title} className="w-full h-full object-cover rounded-lg" data-id="element-1327" />
                      </div>
                      <div className="w-full md:w-3/4" data-id="element-1328">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2" data-id="element-1329">
                          <h3 className="text-xl font-semibold" data-id="element-1330">
                            {project.title}
                          </h3>
                          <div className="bg-leaf-green text-white text-sm font-semibold rounded-full px-2 py-1 whitespace-nowrap" data-id="element-1331">
                            {project.match}% Match
                          </div>
                        </div>
                        <div className="flex items-center text-gray-600 mb-3" data-id="element-1332">
                          <MapPinIcon size={16} className="mr-1 flex-shrink-0" data-id="element-1333" />
                          <span className="text-sm" data-id="element-1334">{project.location}</span>
                        </div>
                        <p className="text-gray-600 mb-4 text-sm md:text-base" data-id="element-1335">
                          {project.description}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-id="element-1336">
                          <div className="flex flex-col" data-id="element-1337">
                            <p className="text-xs text-gray-500" data-id="element-1338">Funding</p>
                            <div className="flex items-center" data-id="element-1339">
                              <DollarSignIcon size={14} className="mr-1 text-soil-brown" data-id="element-1340" />
                              <p className="font-semibold text-soil-brown text-sm" data-id="element-1341">
                                {project.funding}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col" data-id="element-1342">
                            <p className="text-xs text-gray-500" data-id="element-1343">Return</p>
                            <p className="font-semibold text-soil-brown text-sm" data-id="element-1344">
                              {project.return}
                            </p>
                          </div>
                          <div className="flex flex-col" data-id="element-1345">
                            <p className="text-xs text-gray-500" data-id="element-1346">Timeline</p>
                            <div className="flex items-center" data-id="element-1347">
                              <CalendarIcon size={14} className="mr-1 text-soil-brown" data-id="element-1348" />
                              <p className="font-semibold text-soil-brown text-sm" data-id="element-1349">
                                {project.timeline}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3" data-id="element-1350">
                          <div className="flex space-x-2" data-id="element-1351">
                            <button className={`p-2 rounded-full ${likedProjects.includes(project.id) ? 'bg-green-100 text-green-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleLike(project.id)} aria-label="Like" data-id="element-1352">
                              <ThumbsUpIcon size={18} data-id="element-1353" />
                            </button>
                            <button className={`p-2 rounded-full ${dislikedProjects.includes(project.id) ? 'bg-red-100 text-red-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleDislike(project.id)} aria-label="Dislike" data-id="element-1354">
                              <ThumbsDownIcon size={18} data-id="element-1355" />
                            </button>
                            <button className={`p-2 rounded-full ${savedProjects.includes(project.id) ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleSaved(project.id)} aria-label="Save" data-id="element-1356">
                              <BookmarkIcon size={18} data-id="element-1357" />
                            </button>
                          </div>
                          <button className="btn btn-primary w-full sm:w-auto" data-id="element-1358">
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>)}
              </motion.div>}
          </div>}
        {/* List View */}
        {viewMode === 'list' && <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2" data-id="element-1359">
            {projects.filter(project => activeTab !== 'saved' || savedProjects.includes(project.id)).map(project => <motion.div key={project.id} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="card p-0 overflow-hidden flex flex-col sm:flex-row" data-id="element-1360">
                  <div className="w-full sm:w-2/5 h-48 sm:h-auto overflow-hidden" data-id="element-1361">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-1362" />
                  </div>
                  <div className="p-3 md:p-4 flex-1 flex flex-col" data-id="element-1363">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2" data-id="element-1364">
                      <h3 className="text-lg md:text-xl font-semibold" data-id="element-1365">
                        {project.title}
                      </h3>
                      <div className="bg-leaf-green text-white text-xs md:text-sm font-semibold rounded-full px-2 py-1 whitespace-nowrap" data-id="element-1366">
                        {project.match}% Match
                      </div>
                    </div>
                    <div className="flex items-center text-gray-600 mb-2 md:mb-3" data-id="element-1367">
                      <MapPinIcon size={16} className="mr-1 flex-shrink-0" data-id="element-1368" />
                      <span className="text-xs md:text-sm" data-id="element-1369">
                        {project.location}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3 md:mb-4 text-xs md:text-sm line-clamp-2" data-id="element-1370">
                      {project.description}
                    </p>
                    <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4" data-id="element-1371">
                      <div className="flex flex-col" data-id="element-1372">
                        <p className="text-xs text-gray-500" data-id="element-1373">Funding</p>
                        <div className="flex items-center" data-id="element-1374">
                          <DollarSignIcon size={14} className="mr-1 text-soil-brown" data-id="element-1375" />
                          <p className="font-semibold text-soil-brown text-xs md:text-sm" data-id="element-1376">
                            {project.funding}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col" data-id="element-1377">
                        <p className="text-xs text-gray-500" data-id="element-1378">Return</p>
                        <p className="font-semibold text-soil-brown text-xs md:text-sm" data-id="element-1379">
                          {project.return}
                        </p>
                      </div>
                      <div className="flex flex-col" data-id="element-1380">
                        <p className="text-xs text-gray-500" data-id="element-1381">Timeline</p>
                        <div className="flex items-center" data-id="element-1382">
                          <CalendarIcon size={14} className="mr-1 text-soil-brown" data-id="element-1383" />
                          <p className="font-semibold text-soil-brown text-xs md:text-sm" data-id="element-1384">
                            {project.timeline}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 md:gap-2 mb-3 md:mb-4" data-id="element-1385">
                      {project.tags.map((tag, index) => <span key={index} className="bg-wheat-gold/50 text-soil-brown text-xs px-2 py-0.5 rounded-full" data-id="element-1386">
                          {tag}
                        </span>)}
                    </div>
                    <div className="mt-auto flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3" data-id="element-1387">
                      <div className="flex space-x-2" data-id="element-1388">
                        <button className={`p-1.5 md:p-2 rounded-full ${likedProjects.includes(project.id) ? 'bg-green-100 text-green-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleLike(project.id)} aria-label="Like" data-id="element-1389">
                          <ThumbsUpIcon size={16} data-id="element-1390" />
                        </button>
                        <button className={`p-1.5 md:p-2 rounded-full ${dislikedProjects.includes(project.id) ? 'bg-red-100 text-red-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleDislike(project.id)} aria-label="Dislike" data-id="element-1391">
                          <ThumbsDownIcon size={16} data-id="element-1392" />
                        </button>
                        <button className={`p-1.5 md:p-2 rounded-full ${savedProjects.includes(project.id) ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => toggleSaved(project.id)} aria-label="Save" data-id="element-1393">
                          <BookmarkIcon size={16} data-id="element-1394" />
                        </button>
                      </div>
                      <button className="btn btn-primary text-sm py-1.5 w-full sm:w-auto" data-id="element-1395">
                        View Details
                      </button>
                    </div>
                  </div>
                </motion.div>)}
          </div>}
        {/* Project Comparison Tool */}
        {showComparison && <ProjectComparisonTool projects={projects} onClose={() => setShowComparison(false)} data-id="element-1396" />}
      </div>
    </Layout>;
};